import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Drawer from '..';
import DrawerHeader from '../header';
import { Box } from '@mui/material';
import DrawerContent from '../content';
import { useQuery } from 'react-query';
import { pluralize } from '@/lib/helpers';
import useViewMode from '@/hooks/use-view-mode.hook';
import useQueryHelper from '@/hooks/use-query-helper';
import useFormHandle from '@/hooks/use-form-handle.hook';
import {
  ConnectorType,
  ConnectorMap,
  getTypedConnector,
  getConnectorCategoryOptions,
} from '@/lib/models/connector';
import InputControl from '../../form/input-control';
import useToast from '@/hooks/use-toast.hook';
import useModal from '@/hooks/use-modal.hook';
import DrawerButtons from '@/components/form/drawer-buttons';
import {
  createConnector,
  deleteConnector,
  getConnectorById,
  getConnectorsByType,
  getConnectorUsage,
  patchConnector,
} from '@/lib/services/connector.service';

import { CreateResponse, PatchResponse } from '@/lib/services';
import AwsConnector from './aws-connector';
import ConnectorReadonlyView from './readonly-view';
import SharepointConnector from './sharepoint-connector';
import Select from '@/components/form/select';
import { AwsConnectorModel } from '@/lib/models/connector/aws-connector.model';
import { SharepointConnectorModel } from '@/lib/models/connector/sharepoint-connector.model';
import { ConnectorModel } from '@/lib/models/connector/connector.model';
import { QueryKey, refetchQuery } from '@/lib/query-client';
import { Form } from '@/components/form';
import { PrivateModelModel } from '@/lib/models/private-model/private-model.model';
import { ContextProviderModel } from '@/lib/models/context-provider/context-provider.model';
import InlineToast from '@/components/toasts/inline';
import WebcrawlerConnector from './webcrawler-connector';
import { WebcralwerConnectorModel } from '@/lib/models/connector/webcrawler-connector.model';
import useFeature from '@/hooks/use-feature.hook';
import NetskopeConnector from './netskope-connector';
import { NetskopeConnectorModel } from '@/lib/models/connector/netskope-connector.model';
import ZscalerConnector from './zscaler-connector';
import { ZscalerConnectorModel } from '@/lib/models/connector/zscaler-connector.model';
import ApiKeyConnector from './api-key-connector';
import AzureOpenAIConnector from './azure-openai-connector';
import { AzureOpenAIConnectorModel } from '@/lib/models/connector/azure-openai-connector.model';
import AzureFoundryConnector from './azure-foundry-connector';
import { AzureFoundryConnectorModel } from '@/lib/models/connector/azure-foundry-connector.model';
import ProxyConnector from './proxy/proxy-connector';
import { ProxyConnectorModel } from '@/lib/models/connector/proxy-connector.model';
import CloudflareConnector from './cloudflare-connector';
import { CloudflareConnectorModel } from '@/lib/models/connector/cloudflare-connector.model';
import DohConnector from './doh-connector';
import { DohConnectorModel } from '@/lib/models/connector/doh-connector.model';
import CloudflareAIConnector from './cloudflare-ai-connector';
import TailscaleConnector from './tailscale-connector';
import VertexAIConnector from './vertex-ai-connector';
import { DrawerProps } from '../helpers';
import ConnectorMenu from './menu';

interface Props extends DrawerProps {
  id: string | null;
  connectorType?: ConnectorType;
  name?: string;
}

export const ConnectorDrawer: FC<Props> = ({
  id,
  open,
  onClose,
  className,
  connectorType: propConnectorType,
  name: propName = '',
  isTopDrawer,
}) => {
  const [connectorId, setConnectorId] = useState(id || null);
  const [connectorType, setConnectorType] = useState<ConnectorType | ''>(propConnectorType || '');

  const formModel = useMemo(() => {
    const connector = new ConnectorModel({ type: propConnectorType, name: propName });
    return getTypedConnector(connector);
  }, [propName, propConnectorType]);

  const isNew = !connectorId;
  const { mode, setMode, inWriteMode, inAddMode } = useViewMode(isNew ? 'add' : 'view');

  const { toast, errorToast } = useToast();
  const { openModal } = useModal();
  const { canChangeConnector, canViewConnectorArn, canViewDemoConnectors } = useFeature();

  const query = useQuery<[ConnectorModel | null, PrivateModelModel[], ContextProviderModel[]]>(
    [QueryKey.ConnectorMetaView, connectorId],
    async () => {
      const connector = await getConnectorById(connectorId!);

      if (!connector) {
        return [null, [], []];
      }

      const [contexts, models] = await getConnectorUsage(connector.id);

      return [connector, models, contexts];
    },
    {
      enabled: !isNew,
    }
  );

  const { data, refetch } = query;

  const conflictQuery = useQuery(
    [QueryKey.ConnectorsTypeList, connectorType, mode],
    async () => {
      if (!inAddMode) {
        return false;
      }

      if (!connectorType) {
        return false;
      }

      const typedConnector = ConnectorMap.get(connectorType);

      if (!typedConnector?.highlander) {
        return false;
      }

      const existConnectors = await getConnectorsByType(connectorType);

      return !!existConnectors?.length;
    },
    { enabled: !!connectorType && inAddMode }
  );

  const { data: hasConflict } = conflictQuery;
  const { showLoader: conflictLoading } = useQueryHelper(conflictQuery);

  const [connectorData, models, contexts] = data || [null, [], []];

  const connector = useMemo(
    () => connectorData || new ConnectorModel({ type: propConnectorType }),
    [connectorData, propConnectorType]
  );
  const canDelete = useMemo(() => !models.length && !contexts.length, [models, contexts]);

  // you can only create new connectors if you have access to that connector type
  const safeConnectorOptions = useMemo(() => {
    return getConnectorCategoryOptions()
      .filter(({ meta }) => !meta?.demo || canViewDemoConnectors)
      .map((opt) => {
        const { disabled } = opt;
        opt.disabled = disabled || !canChangeConnector;
        return opt;
      });
  }, [canChangeConnector, canViewDemoConnectors]);

  const handleRefresh = () => {
    refetchQuery([
      QueryKey.ConnectorsDataset,
      QueryKey.ConnectorProvidersList,
      QueryKey.ConnectorSiemsList,
      QueryKey.ConnectorModelsList,
      QueryKey.ConnectorsTypeList,
      QueryKey.ConnectorsList,
    ]);
  };

  const formHandle = useFormHandle(
    {
      initialValues: formModel,
      validationSchema: formModel.schema,
      validateOnChange: !isNew,
      onSubmit: async (connector, { setFieldError, resetForm }) => {
        const handleErrors = (response: CreateResponse | PatchResponse): boolean => {
          const { error, fieldErrors } = response;

          if (error) {
            errorToast(error);
            return false;
          }

          if (fieldErrors) {
            fieldErrors.forEach(({ path, msg }) => {
              let fieldName = path;
              if (fieldName in connector.config) {
                fieldName = `config.${fieldName}`;
              }
              if (
                isNew &&
                path === 'crossAccountRoleARN' &&
                !fieldErrors.some(({ path }) => path === 'awsAccountId')
              ) {
                fieldName = 'defaultRoleARN';
              }
              setFieldError(fieldName, msg);
            });
            return false;
          }

          return true;
        };

        if (!connectorType) {
          return;
        }

        connector.type = connectorType;

        if (!connectorId) {
          const response = await createConnector(connector);

          if (!handleErrors(response)) {
            return;
          }

          const newConnector = response.model! as ConnectorModel;

          resetForm({ values: connector });
          toast('The connector was created');
          setConnectorId(newConnector.id);
          setConnectorType(newConnector.type);
          handleRefresh();
          setMode('view');
          return;
        }

        const response = await patchConnector(connector);

        if (!handleErrors(response)) {
          return;
        }

        resetForm({ values: connector });
        toast('The connector was updated');
        refetch();
        handleRefresh();
        setMode('view');
      },
    },
    { lockTarget: 'drawer' }
  );

  const {
    handleSubmit,
    handleBlur,
    canSubmit,
    isSubmitting,
    loadData,
    setValues,
    values,
    errors,
    updateValidationSchema,
  } = formHandle;

  const handleCancel = () => {
    if (isNew) {
      onClose(true);
      return;
    }
    setMode('view');
    loadData(connector);
  };

  const handleEdit = () => {
    setMode('edit');
  };

  const handleChangeConnectorType = useCallback(
    (value: string) => {
      if (!isNew) {
        return;
      }

      const connectorType = value as ConnectorType;
      setConnectorType(connectorType);
      values.type = connectorType;
      const typedConnector = getTypedConnector(values);
      setValues(typedConnector);
      updateValidationSchema(typedConnector.schema);
    },
    [setValues, updateValidationSchema, values, isNew]
  );

  const handleDelete = () => {
    const hasModels = !!models.length;
    const hasContext = !!contexts.length;

    openModal('delete', {
      title: 'Delete connector',
      content: (
        <>
          Are you sure you want to delete {connector.name}?
          {hasModels && (
            <Box mt={2}>
              <InlineToast
                message={`${models.length} ${pluralize(
                  models.length,
                  'Private Model'
                )} will be disconnected`}
                level="warning"
              />
            </Box>
          )}
          {hasContext && (
            <Box mt={2}>
              <InlineToast
                message={`${contexts.length} ${pluralize(
                  contexts.length,
                  'Data Sources'
                )} will be disconnected`}
                level="warning"
              />
            </Box>
          )}
        </>
      ),
      onClose: (confirm: boolean) => {
        if (confirm) {
          deleteConnector(connector).then(({ deleted, error }) => {
            if (!deleted) {
              errorToast(error || 'The connector was not deleted');
              return;
            }

            setConnectorId('');
            setConnectorType('');
            onClose();
            toast('The connector was deleted');
            handleRefresh();
          });
        }
      },
    });
  };

  useEffect(() => {
    if (data?.length) {
      const [connector] = data;
      if (connector) {
        const typedConnector = getTypedConnector(connector);
        loadData(typedConnector);
        updateValidationSchema(typedConnector.schema);
        setConnectorId(connector.id);
        setConnectorType(connector.type);
      }
    }
  }, [data, loadData, updateValidationSchema]);

  const { name } = connector;

  const typedConnector = connectorType ? ConnectorMap.get(connectorType) : null;
  const connectorTypeLabel = typedConnector?.typeLabel || '';
  const comingSoon = !!typedConnector?.comingSoon;

  return (
    <Drawer open={open} onClose={onClose} className={className} query={query} size="medium">
      <DrawerHeader
        onClose={onClose}
        menu={
          <ConnectorMenu
            connector={connector}
            connectorType={connectorType}
            isNew={isNew}
            canViewConnectorArn={canViewConnectorArn}
            showUpdateMenu={!isNew && canChangeConnector && !comingSoon}
            onEdit={handleEdit}
            onDelete={handleDelete}
            canEdit={!inWriteMode}
            canDelete={canDelete}
            deleteSubtitle={canDelete ? '' : 'You cannot delete a connector that is in use'}
          />
        }
        isTopDrawer={isTopDrawer}
      >
        {isNew ? 'Add Connector' : name}
      </DrawerHeader>
      <DrawerContent>
        <Box mt={2}>
          {!inWriteMode && (
            <ConnectorReadonlyView
              connector={connector}
              canViewArn={canViewConnectorArn}
              privateModels={models}
              contextProviders={contexts}
            />
          )}

          <Form formHandle={formHandle}>
            {inWriteMode && (
              <>
                <InputControl name="name" label="Name" formHandle={formHandle} autoFocus />

                <Select
                  name="type"
                  value={connectorType}
                  label="Type"
                  options={safeConnectorOptions}
                  readonly={!isNew}
                  onBlur={handleBlur}
                  onChange={handleChangeConnectorType}
                  error={errors.type}
                  required
                />
              </>
            )}

            {hasConflict && (
              <InlineToast
                level="warning"
                message={`There can be only one active ${connectorTypeLabel} connector. Please remove the existing connector before adding a new one.`}
              />
            )}

            {!hasConflict && !conflictLoading && (
              <>
                {connectorType === 'aws' && (
                  <AwsConnector
                    formHandle={formHandle}
                    connector={getTypedConnector(connector) as AwsConnectorModel}
                    mode={mode}
                  />
                )}

                {connectorType === 'sharepoint-online' && (
                  <SharepointConnector
                    formHandle={formHandle}
                    connector={connector as SharepointConnectorModel}
                    mode={mode}
                  />
                )}

                {connectorType === 'webcrawler' && (
                  <WebcrawlerConnector
                    formHandle={formHandle}
                    connector={connector as WebcralwerConnectorModel}
                    mode={mode}
                  />
                )}

                {connectorType === 'netskope' && (
                  <NetskopeConnector
                    formHandle={formHandle}
                    connector={connector as NetskopeConnectorModel}
                    mode={mode}
                  />
                )}

                {connectorType === 'zscaler' && (
                  <ZscalerConnector
                    formHandle={formHandle}
                    connector={connector as ZscalerConnectorModel}
                    mode={mode}
                  />
                )}

                {connectorType === 'proxy' && (
                  <ProxyConnector
                    formHandle={formHandle}
                    connector={connector as ProxyConnectorModel}
                    mode={mode}
                  />
                )}

                {connectorType === 'cloudflare' && (
                  <CloudflareConnector
                    formHandle={formHandle}
                    connector={connector as CloudflareConnectorModel}
                    mode={mode}
                  />
                )}

                {connectorType === 'doh' && (
                  <DohConnector
                    formHandle={formHandle}
                    connector={connector as DohConnectorModel}
                    mode={mode}
                  />
                )}

                {connectorType === 'cloudflare-ai' && (
                  <CloudflareAIConnector
                    formHandle={formHandle}
                    connector={connector}
                    mode={mode}
                  />
                )}

                {[
                  'openai',
                  'gemini',
                  'brave-search',
                  'perplexity',
                  'nvidia-nim',
                  'anthropic',
                ].includes(connectorType) && (
                  <ApiKeyConnector formHandle={formHandle} connector={connector} mode={mode} />
                )}

                {connectorType === 'azure-openai' && (
                  <AzureOpenAIConnector
                    formHandle={formHandle}
                    connector={connector as AzureOpenAIConnectorModel}
                    mode={mode}
                  />
                )}

                {connectorType === 'azure-foundry' && (
                  <AzureFoundryConnector
                    formHandle={formHandle}
                    connector={connector as AzureFoundryConnectorModel}
                    mode={mode}
                  />
                )}

                {connectorType === 'tailscale' && (
                  <TailscaleConnector formHandle={formHandle} mode={mode} />
                )}

                {connectorType === 'vertex-ai' && (
                  <VertexAIConnector formHandle={formHandle} connector={connector} mode={mode} />
                )}
              </>
            )}

            {inWriteMode && (
              <DrawerButtons
                submit
                cancel
                isNew={isNew}
                canSubmit={canSubmit}
                canCancel={!isSubmitting}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                readonly={hasConflict}
                formHandle={formHandle}
              />
            )}
          </Form>
        </Box>
      </DrawerContent>
    </Drawer>
  );
};

export default ConnectorDrawer;
